import { useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Language } from '@/types/resort';
import { SITE_LANGUAGES } from '@/site';

/** The current site's languages (SiteConfig.languages). */
const VALID_LANGS: readonly string[] = SITE_LANGUAGES;

/**
 * Syncs the URL language prefix with the language context.
 * - On mount: Sets language from URL if valid
 * - On language change: Updates URL prefix
 */
export function LanguageRouter() {
  const { lang } = useParams<{ lang: string }>();
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const prevLanguage = useRef(language);
  const prevLang = useRef(lang);

  // One effect decides which side changed, so URL and context never fight each other.
  useEffect(() => {
    const languageChanged = prevLanguage.current !== language;
    const urlChanged = prevLang.current !== lang;
    prevLanguage.current = language;
    prevLang.current = lang;

    if (!lang || !VALID_LANGS.includes(lang as Language) || lang === language) return;

    if (languageChanged && !urlChanged) {
      // User picked a new language: move the URL to match
      const pathWithoutLang = location.pathname.replace(`/${lang}`, '');
      navigate(`/${language}${pathWithoutLang || '/'}${location.search}${location.hash}`, { replace: true });
    } else {
      // URL is the source of truth (first load, links, back/forward)
      setLanguage(lang as Language);
    }
  }, [lang, language, setLanguage, navigate, location.pathname, location.search, location.hash]);

  return <Outlet />;
}

/** The host serves trailing-slash addresses, so link straight to that form. */
function withTrailingSlash(pathname: string) {
  if (!pathname) return '/';
  return pathname.endsWith('/') ? pathname : `${pathname}/`;
}

/**
 * Redirects from non-language paths to language-prefixed paths
 */
export function LanguageRedirect() {
  const { language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Only redirect on root or non-language paths
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const firstSegment = pathSegments[0];
    
    // If already has a language prefix, don't redirect
    if (VALID_LANGS.includes(firstSegment as Language)) {
      return;
    }

    // Redirect straight to the final language-prefixed, trailing-slash version
    const newPath = `/${language}${withTrailingSlash(location.pathname)}${location.search}${location.hash}`;
    navigate(newPath, { replace: true });
  }, [language, location.pathname, location.search, location.hash, navigate]);

  return null;
}

/**
 * Hook to generate language-aware URLs for internal links
 */
export function useLanguageUrl() {
  const { language } = useLanguage();
  
  return (path: string) => {
    // Ensure path starts with / and ends with the canonical trailing slash
    const [pathname, rest = ''] = path.split(/(?=[?#])/, 2);
    const normalizedPath = pathname.startsWith('/') ? pathname : `/${pathname}`;
    return `/${language}${withTrailingSlash(normalizedPath)}${rest}`;
  };
}

